import React, { CSSProperties } from 'react';
import { DragDropContext, DragDropContextProps, Droppable, DroppableProps } from 'react-beautiful-dnd';
import { BpDesignSystem } from '../../types';
import { Box } from '../Box';
export { DragDropContext };

export type DragAndDropProps = BpDesignSystem.PropsWithChildren<
  Pick<Partial<DragDropContextProps>, 'onDragEnd'> & {
    slotProps?: {
      dragDropContext?: Omit<DragDropContextProps, 'children' | 'onDragEnd'>;
      droppable?: Omit<Partial<DroppableProps>, 'children' | 'droppableId' | 'mode'>;
    };
    droppableId: string;
    mode?: DroppableProps['mode'];
    getStyle?: (isDraggingOver: boolean) => CSSProperties;
    getChildren?: DroppableProps['children'];
    /**
     * It uses the DragDropContext for just one container. If you set this to false, you must provide the DragDropContext on top of this component.
     * default: true
     */
    singleContainer?: boolean;
  }
>;

export const DragAndDrop: React.FC<DragAndDropProps> = ({
  children,
  getChildren,
  className,
  dataTest,
  style,
  getStyle,
  sx,
  droppableId,
  slotProps,
  onDragEnd,
  mode,
  singleContainer = true
}) => {
  const isVirtual = mode === 'virtual';
  if (isVirtual && !slotProps?.droppable?.renderClone) {
    // biome-ignore lint/suspicious/noConsole: warn needed
    console.warn(`slotProps?.droppable?.renderClone must be provided when mode is virtual.`);
  }

  const content = (
    <Droppable
      droppableId={droppableId}
      mode={mode}
      {...slotProps?.droppable}
      renderClone={isVirtual && slotProps?.droppable?.renderClone ? slotProps?.droppable?.renderClone : undefined}
    >
      {(provided, snapshot) => {
        if (children) {
          return (
            <Box
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={className}
              dataTest={dataTest}
              sx={sx}
              style={{
                ...style,
                ...(getStyle?.(snapshot.isDraggingOver) ?? {})
              }}
            >
              {children}
              {provided.placeholder}
            </Box>
          );
        }
        if (getChildren) {
          const element = getChildren(provided, snapshot) as React.ReactElement<HTMLElement>;
          if (element) {
            return element;
          }
        }
        return <div ref={provided.innerRef} />;
      }}
    </Droppable>
  );
  if (!singleContainer) {
    return content;
  }
  return (
    <DragDropContext onDragEnd={onDragEnd ?? noop} {...slotProps?.dragDropContext}>
      {content}
    </DragDropContext>
  );
};

function noop() {}
